import { format, parse, isValid } from 'date-fns';

const SHIFT_TYPES = ['früh', 'zwischen', 'spät'];
const SHIFT_CODES = { '1': 'früh', '2': 'zwischen', '3': 'spät', 'F': 'holiday' };

/**
 * Parse a CSV file and convert it back to a schedule object AND employee list
 * @param {string} csvText - The CSV text content
 * @param {string} departmentId - The department ID
 * @param {Array} existingEmployees - List of currently loaded employees to match against
 * @returns {Object} - { schedule, employees }
 */
export function parseCSVSchedule(csvText, departmentId, existingEmployees = []) {
  try {
    const lines = csvText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    // 1. Detect format
    const isVerticalFormat = lines.some(line => line.includes('Mitarbeiter') && line.includes('Arbeitspensum') && !line.includes('Statistiken'));
    
    // 2. Extract Employees first (needed for ID mapping)
    // We try to find the "Mitarbeiter Statistiken" section or parse from rows depending on format
    const { employees, nameToIdMap } = extractEmployeesFromCSV(lines, existingEmployees, departmentId);

    // 3. Parse Schedule Data using the map
    let scheduleData;
    if (isVerticalFormat) {
      scheduleData = parseVerticalCSV(lines, departmentId, nameToIdMap);
    } else {
      scheduleData = parseHorizontalCSV(lines, departmentId, nameToIdMap);
    }

    return {
      schedule: scheduleData,
      employees: employees
    };

  } catch (error) {
    console.error('CSV parsing error:', error);
    throw new Error(`CSV-Datei konnte nicht verarbeitet werden: ${error.message}`);
  }
}

/**
 * Extract employees from the CSV to ensure we have a valid list for the view
 */
function extractEmployeesFromCSV(lines, existingEmployees, departmentId) {
  const employees = [...existingEmployees]; // Start with existing to keep IDs if possible
  const nameToIdMap = new Map();

  // Populate map with existing employees first
  existingEmployees.forEach(emp => {
    if (emp.name) {
      nameToIdMap.set(emp.name.toLowerCase().trim(), emp.id);
    }
  });

  const newEmployees = [];

  // Helper to get or create employee
  const getOrCreateEmployee = (name, workload = 100) => {
    const cleanName = name.trim();
    const cleanNameLower = cleanName.toLowerCase();
    
    if (nameToIdMap.has(cleanNameLower)) {
      return; // Already exists
    }

    // Create new temporary employee
    const newId = `imported_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newEmp = {
      id: newId,
      name: cleanName,
      department_id: departmentId,
      workload: workload,
      skills: ['früh', 'zwischen', 'spät'], // Default assumption
      preferences: [],
      availableDays: { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: true, sunday: true }
    };

    newEmployees.push(newEmp);
    nameToIdMap.set(cleanNameLower, newId);
  };

  // Strategy A: Try to find "Mitarbeiter Statistiken" section (Horizontal & Vertical exports usually have this)
  const statsStartIndex = lines.findIndex(line => line.startsWith('Mitarbeiter Statistiken'));
  if (statsStartIndex !== -1) {
    let i = statsStartIndex + 2; // Skip header
    while (i < lines.length && lines[i].trim() !== '' && !lines[i].startsWith('Regelabweichungen') && !lines[i].startsWith('Detaillierte')) {
      const line = lines[i];
      const cells = line.split(',').map(c => c.trim());
      // Expected: Name, Früh, Zwischen, Spät, Tage, Stunden, Soll, Pensum
      if (cells.length >= 8) {
        const name = cells[0];
        const workloadStr = cells[7].replace('%', '');
        const workload = parseInt(workloadStr) || 100;
        getOrCreateEmployee(name, workload);
      }
      i++;
    }
  }

  // Strategy B: If vertical, scan rows for names
  const isVertical = lines.some(line => line.includes('Mitarbeiter') && line.includes('Arbeitspensum'));
  if (isVertical) {
    let i = 0;
    while(i < lines.length) {
      const line = lines[i];
      // Vertical row start looks like: "Name, 100% | 160h / 160h, ..."
      // But we need to be careful not to match headers.
      // The Vertical export structure in `VerticalScheduleView` puts name in first col.
      // But the CSV export from `ScheduleExcel` puts "Mitarbeiter" in header.
      // Let's rely on the specific format from `ScheduleExcel` which is horizontal-ish for data but has a stats section.
      // If the user uploads a CSV generated by `VerticalScheduleView` print/export, it might be different.
      // Assuming `ScheduleExcel` format primarily.
      i++;
    }
  }

  return {
    employees: [...employees, ...newEmployees],
    nameToIdMap
  };
}

/**
 * Parse horizontal format CSV (date-based rows)
 */
function parseHorizontalCSV(lines, departmentId, nameToIdMap) {
  const schedule = {
    id: `uploaded_${Date.now()}`,
    month: '',
    schedule: {},
    violations: [],
    employeeHours: {},
    employeeStats: {},
    targetHours: {},
    employeeWeekendShifts: {},
    explanations: [],
    daysWithoutZwischendienst: [],
    createdAt: new Date().toISOString(),
    departmentId: departmentId,
    viewMode: 'horizontal'
  };

  // Find the schedule data section
  const scheduleStartIndex = lines.findIndex(line => line.startsWith('Datum,'));
  if (scheduleStartIndex === -1) {
    throw new Error('Keine Schedule-Daten gefunden. Die Datei muss mit "Datum," beginnen.');
  }

  // Extract month from title line
  const titleLine = lines.find(line => line.includes('Dienstplan'));
  if (titleLine) {
    const monthMatch = titleLine.match(/Dienstplan.*?-\s*([A-Za-z]+\s+\d{4})/);
    if (monthMatch) {
      const monthName = monthMatch[1];
      const monthDate = parse(monthName, 'MMMM yyyy', new Date());
      if (isValid(monthDate)) {
        schedule.month = format(monthDate, 'yyyy-MM');
      }
    }
  }

  // Parse schedule data
  let currentLine = scheduleStartIndex + 1;
  while (currentLine < lines.length && !lines[currentLine].startsWith('Mitarbeiter Statistiken')) {
    const line = lines[currentLine];
    if (line.trim() === '' || line.startsWith('Datum')) { 
      currentLine++; 
      continue; 
    }
    
    const cells = line.split(',').map(c => c.trim());
    if (cells.length >= 5) {
      const dateStr = cells[0]; // dd.MM.yyyy
      const fruehCell = cells[2];
      const zwischenCell = cells[3];
      const spaetCell = cells[4];

      const date = parse(dateStr, 'dd.MM.yyyy', new Date());
      if (isValid(date)) {
        const formattedDate = format(date, 'yyyy-MM-dd');
        schedule.schedule[formattedDate] = {};

        const mapNamesToIds = (cellContent) => {
          if (!cellContent || cellContent === '-' || cellContent === 'Nicht besetzt' || cellContent === 'Entfällt' || cellContent === 'Zwischendienst entfällt') return [];
          return cellContent.split(';')
            .map(n => n.trim())
            .filter(n => n && n !== '-')
            .map(n => nameToIdMap.get(n.toLowerCase()) || null) // Map name to ID
            .filter(id => id !== null);
        };

        const fruehIds = mapNamesToIds(fruehCell);
        if (fruehIds.length > 0) schedule.schedule[formattedDate].früh = fruehIds;

        if (zwischenCell === 'Zwischendienst entfällt' || zwischenCell === 'Entfällt') {
          schedule.daysWithoutZwischendienst.push(formattedDate);
        } else {
          const zwischenIds = mapNamesToIds(zwischenCell);
          if (zwischenIds.length > 0) schedule.schedule[formattedDate].zwischen = zwischenIds;
        }

        const spaetIds = mapNamesToIds(spaetCell);
        if (spaetIds.length > 0) schedule.schedule[formattedDate].spät = spaetIds;
      }
    }
    currentLine++;
  }

  // Parse employee statistics for hours restoration
  const statsStartIndex = lines.findIndex(line => line.startsWith('Mitarbeiter Statistiken'));
  if (statsStartIndex !== -1) {
    let statsLine = statsStartIndex + 2; 
    while (statsLine < lines.length && lines[statsLine].trim() !== '' && !lines[statsLine].includes('Regelabweichungen')) {
      const line = lines[statsLine];
      const cells = line.split(',').map(c => c.trim());
      if (cells.length >= 7) {
        const name = cells[0];
        const empId = nameToIdMap.get(name.toLowerCase());
        
        if (empId) {
          const totalDays = parseInt(cells[4]) || 0;
          const actualHours = parseFloat(cells[5].replace('h', '').replace('min', '').replace(' ', '.'));
          const targetHours = parseFloat(cells[6].replace('h', '').replace('min', '').replace(' ', '.'));
          
          schedule.employeeStats[empId] = {
            früh: parseInt(cells[1]) || 0,
            zwischen: parseInt(cells[2]) || 0,
            spät: parseInt(cells[3]) || 0,
            totalDays: totalDays
          };
          schedule.employeeHours[empId] = actualHours || 0;
          schedule.targetHours[empId] = targetHours || 0;
        }
      }
      statsLine++;
    }
  }

  // Fallback for month
  if (!schedule.month && Object.keys(schedule.schedule).length > 0) {
    const firstDate = Object.keys(schedule.schedule)[0];
    const date = parse(firstDate, 'yyyy-MM-dd', new Date());
    if (isValid(date)) {
      schedule.month = format(date, 'yyyy-MM');
    }
  }

  return schedule;
}

/**
 * Parse vertical format CSV (if structured differently)
 * Assuming similar structure but different layout if implemented later.
 * For now, `ScheduleExcel` exports one format, so `parseHorizontalCSV` covers it.
 * Keeping this stub or using horizontal as fallback.
 */
function parseVerticalCSV(lines, departmentId, nameToIdMap) {
  // If the structure is fundamentally different, implement here.
  // For the current export, it's the same CSV structure.
  const schedule = parseHorizontalCSV(lines, departmentId, nameToIdMap);
  schedule.viewMode = 'vertical';
  return schedule;
}